import { useState, useCallback, useEffect, useRef } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover'
import { Loader2Icon, ActivityIcon, NetworkIcon, EyeIcon, EyeOffIcon, AlertTriangleIcon, SearchIcon, XIcon } from 'lucide-react'
import type { AppException } from '@/types/app-exceptions'

interface Project {
  id: number
  name: string
}

interface Deployment {
  id: number
  status: string
  completed_phases: string[]
  ecs_private_ip: string | null
  started_at: string | null
  completed_at: string | null
  error_log: string | null
  git_ref: string | null
}

interface DevInstance {
  id: number
  name: string
  production_status: string
  deploy_tier: string | null
  compute_tier: string | null
  django_cloudfront_distribution_id: string | null
  django_cloudfront_domain: string | null
  django_ecr_repository_uri: string | null
  django_ecs_web_service_arn: string | null
  django_ecs_worker_service_arn: string | null
  django_ecs_private_ip: string | null
  django_frontend_url: string | null
  django_api_url: string | null
  django_database_name: string | null
  django_deployed_at: string | null
  production_deployed_at: string | null
  production_error_log: string | null
  vanity_subdomain: string | null
  project?: Project
  current_deployment?: Deployment
}

interface HealthResult {
  ecs_web?: { status: string; running?: number; desired?: number }
  ecs_worker?: { status: string; running?: number; desired?: number }
  dns?: { status: string }
  s3_policy?: { status: string }
  cloudfront?: { status: string }
  ecr?: { status: string }
  error?: string
}

interface Pagy {
  page: number
  pages: number
  count: number
  prev: number | null
  next: number | null
}

interface Props {
  dev_instances: DevInstance[]
  pagy: Pagy
  show_all: boolean
  auth: {
    user: { id: number; name: string; email: string; admin: boolean; super_admin?: boolean }
  }
  flash?: { success?: string; error?: string }
}

type HealthStatus = 'healthy' | 'degraded' | 'missing' | 'error' | 'not_created' | 'scaled_down' | 'inactive' | 'disabled' | 'deploying' | 'exists' | 'unknown'

function getHealthBadgeVariant(status: HealthStatus): "success" | "warning" | "destructive" | "secondary" | "info" {
  switch (status) {
    case 'healthy':
    case 'exists':
      return 'success'
    case 'degraded':
    case 'deploying':
      return 'warning'
    case 'missing':
    case 'error':
      return 'destructive'
    case 'scaled_down':
    case 'inactive':
    case 'disabled':
    case 'not_created':
      return 'secondary'
    default:
      return 'info'
  }
}

interface TraefikRoute {
  rule: string
  service: string
  ip: string
}

interface ProjectResult {
  id: number
  name: string
  organization_name?: string
}

function SearchPopover<T extends { id: number; name: string }>({
  selected,
  onSelect,
  onClear,
  placeholder,
  searchUrl,
  renderResult,
}: {
  selected: { id: string; name: string } | null
  onSelect: (result: T) => void
  onClear: () => void
  placeholder: string
  searchUrl: string
  renderResult: (result: T) => React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<T[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.length < 2) {
      setResults([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await fetch(`${searchUrl}?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json' },
        })
        if (response.ok) {
          const data = await response.json()
          setResults(data)
        }
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, open, searchUrl])

  const handleSelect = (result: T) => {
    onSelect(result)
    setOpen(false)
    setQuery('')
    setResults([])
  }

  if (selected) {
    return (
      <div className="flex items-center gap-1 h-6 px-2 text-xs border rounded-md bg-muted">
        <span className="truncate max-w-[120px]">{selected.name}</span>
        <button onClick={onClear} className="shrink-0 hover:text-foreground text-muted-foreground">
          <XIcon className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-6 px-2 text-xs gap-1">
          <SearchIcon className="h-3 w-3" />
          {placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="flex items-center border-b px-3">
          <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isSearching && (
            <div className="flex items-center justify-center py-4">
              <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!isSearching && query.length >= 2 && results.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">No results</p>
          )}
          {!isSearching && query.length < 2 && (
            <p className="py-4 text-center text-sm text-muted-foreground">Type to search...</p>
          )}
          {results.map((result) => (
            <button
              key={result.id}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent cursor-pointer"
              onClick={() => handleSelect(result)}
            >
              {renderResult(result)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function Index({ dev_instances, pagy, show_all }: Props) {
  const { t } = useTranslation('admin')
  const [activeAdminTab, setActiveAdminTab] = useState<'instances' | 'exceptions'>('instances')
  const [searchValue, setSearchValue] = useState('')
  const [healthResults, setHealthResults] = useState<Record<number, HealthResult>>({})
  const [checkingHealth, setCheckingHealth] = useState(false)
  const [showTraefikDialog, setShowTraefikDialog] = useState(false)
  const [traefikConfig, setTraefikConfig] = useState<{ routers: Record<string, TraefikRoute> } | null>(null)
  const [exceptions, setExceptions] = useState<(AppException & { project_id?: number; project_name?: string; dev_instance_id?: number })[]>([])
  const [exceptionsLoading, setExceptionsLoading] = useState(false)
  const [exceptionsStatusFilter, setExceptionsStatusFilter] = useState('unresolved')
  const [loadingTraefik, setLoadingTraefik] = useState(false)
  const [traefikSearch, setTraefikSearch] = useState('')
  const [selectedProject, setSelectedProject] = useState<{ id: string; name: string } | null>(null)

  const handleSearch = useCallback(() => {
    router.get('/admin/django_infra', { q: { name_cont: searchValue } }, { preserveState: true })
  }, [searchValue])

  const handleCheckAllHealth = useCallback(async () => {
    setCheckingHealth(true)
    try {
      const ids = dev_instances.map(d => d.id)
      const response = await fetch('/admin/django_infra/bulk_health_check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ dev_instance_ids: ids }),
      })
      const data = await response.json()
      setHealthResults(data)
    } catch (err) {
      console.error('Health check failed:', err)
    } finally {
      setCheckingHealth(false)
    }
  }, [dev_instances])

  const handleViewTraefikConfig = useCallback(async () => {
    setShowTraefikDialog(true)
    setLoadingTraefik(true)
    try {
      const response = await fetch('/admin/django_infra/traefik_config')
      const data = await response.json()
      setTraefikConfig(data)
    } catch (err) {
      console.error('Failed to fetch Traefik config:', err)
    } finally {
      setLoadingTraefik(false)
    }
  }, [])

  const fetchExceptions = useCallback(async (status: string, projectId?: string | null) => {
    setExceptionsLoading(true)
    try {
      let url = `/admin/django_infra/exceptions?status=${status}`
      if (projectId) url += `&project_id=${projectId}`
      const response = await fetch(url)
      if (response.ok) {
        const data = await response.json()
        setExceptions(data.exceptions || [])
      }
    } catch {
      // silently handle
    } finally {
      setExceptionsLoading(false)
    }
  }, [])

  const handleExceptionsTabClick = useCallback(() => {
    setActiveAdminTab('exceptions')
    fetchExceptions(exceptionsStatusFilter, selectedProject?.id)
  }, [fetchExceptions, exceptionsStatusFilter, selectedProject])

  const handleResolveException = useCallback(async (instanceId: number, exceptionId: number) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    await fetch(`/admin/django_infra/${instanceId}/resolve_exception`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
      body: JSON.stringify({ exception_id: exceptionId }),
    })
    fetchExceptions(exceptionsStatusFilter, selectedProject?.id)
  }, [fetchExceptions, exceptionsStatusFilter, selectedProject])

  const handleIgnoreException = useCallback(async (instanceId: number, exceptionId: number) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    await fetch(`/admin/django_infra/${instanceId}/ignore_exception`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken || '' },
      body: JSON.stringify({ exception_id: exceptionId }),
    })
    fetchExceptions(exceptionsStatusFilter, selectedProject?.id)
  }, [fetchExceptions, exceptionsStatusFilter, selectedProject])

  const getHealthCell = (instanceId: number, resource: keyof HealthResult) => {
    const result = healthResults[instanceId]
    if (!result) return <Badge variant="secondary">-</Badge>
    if (result.error) return <Badge variant="destructive">err</Badge>

    const check = result[resource]
    if (!check || typeof check === 'string') return <Badge variant="secondary">-</Badge>

    const status = check.status as HealthStatus
    const variant = getHealthBadgeVariant(status)
    const label = t(`djangoInfra.health.${status}`, { defaultValue: status })

    if (resource === 'ecs_web' || resource === 'ecs_worker') {
      const ecsCheck = check as { status: string; running?: number; desired?: number }
      if (ecsCheck.running !== undefined) {
        return <Badge variant={variant}>{ecsCheck.running}/{ecsCheck.desired}</Badge>
      }
    }

    return <Badge variant={variant}>{label}</Badge>
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "success" | "warning" | "destructive" | "secondary" | "info"> = {
      deployed: 'success',
      deploying: 'info',
      deploy_failed: 'destructive',
      not_deployed: 'secondary',
    }
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Compact Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                {t('djangoInfra.title')} ({pagy.count})
              </span>
              <div className="w-px h-4 bg-border" />
              <Input
                placeholder={t('djangoInfra.search.placeholder')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="h-7 text-xs w-40"
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                {t('djangoInfra.search.button')}
              </Button>
              <div className="w-px h-4 bg-border" />
              <Button
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={handleCheckAllHealth}
                disabled={checkingHealth}
              >
                {checkingHealth ? (
                  <><Loader2Icon className="h-3 w-3 mr-1 animate-spin" />{t('djangoInfra.health.checking')}</>
                ) : (
                  <><ActivityIcon className="h-3 w-3 mr-1" />{t('djangoInfra.actions.checkAllHealth')}</>
                )}
              </Button>
              <Button variant="outline" className="h-7 px-2 text-xs" onClick={handleViewTraefikConfig}>
                <NetworkIcon className="h-3 w-3 mr-1" />
                {t('djangoInfra.actions.viewTraefikConfig')}
              </Button>
              <Button
                variant={show_all ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => router.get('/admin/django_infra', { show_all: show_all ? undefined : 'true' }, { preserveState: true })}
              >
                {show_all ? (
                  <><EyeOffIcon className="h-3 w-3 mr-1" />{t('djangoInfra.actions.showDeployed')}</>
                ) : (
                  <><EyeIcon className="h-3 w-3 mr-1" />{t('djangoInfra.actions.showAll')}</>
                )}
              </Button>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-1 border-b border-border pb-1.5 mb-2">
              <Button
                variant={activeAdminTab === 'instances' ? 'default' : 'ghost'}
                className="h-7 px-2 text-xs"
                onClick={() => setActiveAdminTab('instances')}
              >
                {t('djangoInfra.tabs.instances')}
              </Button>
              <Button
                variant={activeAdminTab === 'exceptions' ? 'default' : 'ghost'}
                className="h-7 px-2 text-xs"
                onClick={handleExceptionsTabClick}
              >
                <AlertTriangleIcon className="h-3 w-3 mr-1" />
                {t('djangoInfra.tabs.exceptions')}
              </Button>
            </div>

            {/* Exceptions Tab */}
            {activeAdminTab === 'exceptions' && (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
                  {(['unresolved', 'resolved', 'ignored'] as const).map(status => (
                    <Button
                      key={status}
                      variant={exceptionsStatusFilter === status ? 'default' : 'ghost'}
                      className="h-6 px-2 text-xs"
                      onClick={() => { setExceptionsStatusFilter(status); fetchExceptions(status, selectedProject?.id) }}
                    >
                      {t(`djangoInfra.exceptions.status.${status}`)}
                    </Button>
                  ))}
                  <div className="ml-auto">
                    <SearchPopover<ProjectResult>
                      selected={selectedProject}
                      onSelect={(result) => {
                        const proj = { id: String(result.id), name: result.name }
                        setSelectedProject(proj)
                        fetchExceptions(exceptionsStatusFilter, proj.id)
                      }}
                      onClear={() => {
                        setSelectedProject(null)
                        fetchExceptions(exceptionsStatusFilter)
                      }}
                      placeholder={t('djangoInfra.exceptions.filterProject')}
                      searchUrl="/admin/projects/search"
                      renderResult={(r) => (
                        <div className="flex flex-col items-start min-w-0 flex-1">
                          <span className="truncate w-full">{r.name}</span>
                          {r.organization_name && (
                            <span className="text-xs text-muted-foreground truncate w-full">{r.organization_name}</span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
                {exceptionsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Loader2Icon className="h-6 w-6 animate-spin mx-auto" />
                  </div>
                ) : exceptions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-xs">
                    {t('djangoInfra.exceptions.empty')}
                  </div>
                ) : (
                  <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                    <table className="w-full">
                      <thead className="bg-muted sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.exceptions.columns.project')}</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.exceptions.columns.exception')}</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.exceptions.columns.location')}</th>
                          <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">{t('djangoInfra.exceptions.columns.count')}</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.exceptions.columns.lastSeen')}</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.exceptions.columns.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exceptions.map(exc => (
                          <tr key={exc.id} className="border-b border-border hover:bg-muted/30">
                            <td className="px-3 py-1.5">
                              <Link href={`/admin/projects/${exc.project_id}`} className="text-xs text-blue-600 hover:underline">
                                {exc.project_name}
                              </Link>
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="text-xs font-medium text-destructive">{exc.exception_class}</div>
                              <div className="text-xs text-muted-foreground truncate max-w-xs">{exc.message}</div>
                            </td>
                            <td className="px-3 py-1.5">
                              {exc.location && <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{exc.location}</code>}
                            </td>
                            <td className="px-3 py-1.5 text-right text-xs font-mono">{exc.occurrence_count}</td>
                            <td className="px-3 py-1.5 text-xs text-muted-foreground">{new Date(exc.last_seen_at).toLocaleString()}</td>
                            <td className="px-3 py-1.5">
                              {exc.status === 'unresolved' && (
                                <div className="flex gap-1">
                                  <Button className="h-6 px-2 text-xs" variant="outline" onClick={() => handleResolveException(exc.dev_instance_id!, exc.id)}>
                                    {t('djangoInfra.exceptions.actions.resolve')}
                                  </Button>
                                  <Button className="h-6 px-2 text-xs" variant="ghost" onClick={() => handleIgnoreException(exc.dev_instance_id!, exc.id)}>
                                    {t('djangoInfra.exceptions.actions.ignore')}
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Instances Tab */}
            {activeAdminTab === 'instances' && (
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.project')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.instance')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.vanity')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.status')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.deployTier')}</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.ecsWeb')}</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.ecsWorker')}</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.dns')}</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.s3Policy')}</th>
                        <th className="px-3 py-1.5 text-center text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.cloudfront')}</th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.deployedAt')}</th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">{t('djangoInfra.columns.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dev_instances.map((instance) => (
                        <tr key={instance.id} className="border-b border-border hover:bg-muted/25">
                          <td className="px-3 py-1.5">
                            {instance.project ? (
                              <Link href={`/admin/projects/${instance.project.id}`} className="text-xs text-blue-600 hover:underline">
                                {instance.project.name}
                              </Link>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 font-mono text-xs">{instance.name}</td>
                          <td className="px-3 py-1.5 font-mono text-xs">
                            {instance.vanity_subdomain ? (
                              <a href={`https://${instance.vanity_subdomain}.cayu.app`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {instance.vanity_subdomain}.cayu.app
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5">{getStatusBadge(instance.production_status)}</td>
                          <td className="px-3 py-1.5">
                            <div className="flex items-center gap-1">
                              {instance.deploy_tier ? (
                                <Badge variant="secondary">{t(`djangoInfra.tiers.${instance.deploy_tier}`, { defaultValue: instance.deploy_tier })}</Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                              {instance.compute_tier && instance.compute_tier !== 'hobby' && (
                                <Badge variant="outline" className="text-[10px]">{instance.compute_tier}</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-center">{getHealthCell(instance.id, 'ecs_web')}</td>
                          <td className="px-3 py-1.5 text-center">{getHealthCell(instance.id, 'ecs_worker')}</td>
                          <td className="px-3 py-1.5 text-center">{getHealthCell(instance.id, 'dns')}</td>
                          <td className="px-3 py-1.5 text-center">{getHealthCell(instance.id, 's3_policy')}</td>
                          <td className="px-3 py-1.5 text-center">{getHealthCell(instance.id, 'cloudfront')}</td>
                          <td className="px-3 py-1.5 text-xs text-muted-foreground">
                            {instance.production_deployed_at
                              ? new Date(instance.production_deployed_at).toLocaleDateString()
                              : '-'}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <Button variant="ghost" className="h-6 px-2 text-xs" asChild>
                              <Link href={`/admin/django_infra/${instance.id}`}>
                                {t('djangoInfra.actions.viewDetails')}
                              </Link>
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {dev_instances.length === 0 && (
                        <tr>
                          <td colSpan={12} className="px-3 py-8 text-center text-xs text-muted-foreground">
                            {t('djangoInfra.instances.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagy.pages > 1 && (
                  <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                    <span className="text-muted-foreground">
                      Page {pagy.page} of {pagy.pages} ({pagy.count} total)
                    </span>
                    <div className="flex gap-1">
                      {pagy.prev && (
                        <Button
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/django_infra', { page: pagy.prev, show_all: show_all ? 'true' : undefined }, { preserveState: true })}
                        >
                          {t('djangoInfra.pagination.previous')}
                        </Button>
                      )}
                      {pagy.next && (
                        <Button
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/django_infra', { page: pagy.next, show_all: show_all ? 'true' : undefined }, { preserveState: true })}
                        >
                          {t('djangoInfra.pagination.next')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Traefik Config Dialog */}
      <Dialog open={showTraefikDialog} onOpenChange={setShowTraefikDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('djangoInfra.traefik.title')}</DialogTitle>
            <DialogDescription>{t('djangoInfra.traefik.description')}</DialogDescription>
          </DialogHeader>
          {loadingTraefik ? (
            <div className="flex items-center justify-center p-8">
              <Loader2Icon className="h-6 w-6 animate-spin" />
            </div>
          ) : traefikConfig ? (
            <div className="space-y-3">
              <Input
                placeholder={t('djangoInfra.traefik.searchPlaceholder')}
                value={traefikSearch}
                onChange={(e) => setTraefikSearch(e.target.value)}
                className="max-w-sm"
              />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2 font-medium">{t('djangoInfra.traefik.router')}</th>
                      <th className="text-left p-2 font-medium">{t('djangoInfra.traefik.host')}</th>
                      <th className="text-left p-2 font-medium">{t('djangoInfra.traefik.ip')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(traefikConfig.routers)
                      .filter(([name, route]) => {
                        if (!traefikSearch) return true
                        const q = traefikSearch.toLowerCase()
                        return name.toLowerCase().includes(q) || route.rule.toLowerCase().includes(q) || route.ip.toLowerCase().includes(q)
                      })
                      .map(([name, route]) => (
                        <tr key={name} className="border-b">
                          <td className="p-2 font-mono text-xs">{name}</td>
                          <td className="p-2 font-mono text-xs">{route.rule}</td>
                          <td className="p-2 font-mono text-xs">{route.ip}</td>
                        </tr>
                      ))}
                    {Object.keys(traefikConfig.routers).length === 0 && (
                      <tr>
                        <td colSpan={3} className="p-4 text-center text-muted-foreground">
                          {t('djangoInfra.traefik.noRoutes')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
